import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { PasswordStrength, scorePassword } from "@/components/auth/PasswordStrength";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <AuthLayout
        testId="reset-password-no-token"
        eyebrow="Reset password"
        heading="That link is incomplete."
        description="Please request a fresh reset link."
      >
        <Button
          asChild
          className="w-full bg-foreground hover:bg-charcoal text-ivory rounded-none h-12"
        >
          <Link to="/forgot-password">Request a new link</Link>
        </Button>
      </AuthLayout>
    );
  }

  const passwordOk = scorePassword(password).score >= 1;
  const canSubmit = passwordOk && password === confirm && !submitting;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(
        "/auth/reset-password",
        { token, new_password: password },
        { auth: false }
      );
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Reset failed.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <AuthLayout
        testId="reset-password-done"
        eyebrow="Done"
        heading="Password updated."
        description="Redirecting you to sign in…"
      >
        <Button
          asChild
          className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12"
        >
          <Link to="/login">Sign in now</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      testId="reset-password-page"
      eyebrow="Reset password"
      heading="Choose a new password."
      description="At least 8 characters with letters and numbers."
    >
      <form
        data-testid="reset-form"
        onSubmit={onSubmit}
        className="space-y-5"
        noValidate
      >
        <div className="space-y-1.5">
          <Label htmlFor="password">New password</Label>
          <Input
            id="password"
            type="password"
            data-testid="reset-password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            autoComplete="new-password"
            required
            minLength={8}
          />
          <PasswordStrength password={password} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm new password</Label>
          <Input
            id="confirm"
            type="password"
            data-testid="reset-confirm"
            value={confirm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setConfirm(e.target.value)
            }
            autoComplete="new-password"
            required
            minLength={8}
          />
        </div>

        {error && (
          <p
            role="alert"
            data-testid="reset-error"
            className="text-sm text-oxblood border border-oxblood/30 bg-oxblood/5 px-3 py-2"
          >
            {error}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          data-testid="reset-submit"
          disabled={!canSubmit}
          className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12"
        >
          {submitting ? "Updating…" : "Update password"}
        </Button>
      </form>
    </AuthLayout>
  );
}
